/**
 * Neuroimage files (`app/services/neuroimage_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import * as client from '@brainfocus/web/api/client';
import { volumeStore } from '@brainfocus/web/api/volume_store';
import { map, Observable } from 'rxjs';
import { assertPojo } from '@brainfocus/web/api/response';
import type { NeuroimageFileType, NeuroimageFilesResponse } from '@brainfocus/web/api/types';

/**
 * Upload a neuroimage file for a patient (pre-MRI, post-CT/MRI, or implantation plan)
 */
export function uploadNeuroimage(payload: UploadNeuroimagePayload): Observable<client.ResponseProgress<UploadNeuroimageResult>> {
    const formData: Record<string, File | File[] | string> = {
        file: payload.files,
        fileType: payload.fileType,
        coregistrationId: String(payload.coregistrationId),
    };
    return client.requestWithProgress<{ id: number; filename: string; fileType: string }>(
        `/api/patient/${payload.patientId}/upload-neuroimage`,
        formData,
    ).pipe(
        map((v) => {
            if (v.result) {
                return {
                    result: {
                        id: v.result.id,
                        filename: v.result.filename,
                        fileType: v.result.fileType as NeuroimageFileType,
                    },
                };
            } else {
                return { progress: v.progress };
            }
        }),
    );
}

/**
 * Get all neuroimage files for a patient, grouped by type
 */
export async function getNeuroimageFiles(patientId: number): Promise<NeuroimageFilesResponse> {
    const response = await client.request('GET', `/api/patient/${patientId}/neuroimage-files`);
    assertPojo<NeuroimageFilesResponse>(response);
    return response;
}

/**
 * Delete a neuroimage file
 */
export async function deleteNeuroimageFile(fileId: number): Promise<void> {
    await client.request('DELETE', `/api/neuroimage-file/${fileId}`);
    volumeStore.invalidateNeuroimage(fileId);
}

/**
 * Download a neuroimage file (SuperAdmin only)
 * Triggers browser download
 */
export function downloadNeuroimageFile(fileId: number): void {
    // Use window.location to trigger download with authentication
    window.location.href = `/api/neuroimage-file/${fileId}/download`;
}

export interface UploadNeuroimagePayload {
    patientId: number;
    files: File[];
    fileType: NeuroimageFileType;
    coregistrationId: number;
}

export interface UploadNeuroimageResult {
    id: number;
    filename: string;
    fileType: NeuroimageFileType;
}
